import { useState, useRef, useEffect } from "react";
import { Mic, Square, Loader2, MicOff, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * VoiceRecordButton (Feb-2026, User-Wunsch) — nimmt per Mikrofon auf, schickt die
 * Aufnahme an /assistant/transcribe (Whisper) und liefert den erkannten Text via
 * onText zurück. Ermöglicht „das Problem per Sprache besprechen".
 *
 * Jun-2026: Robuste Mikrofon-Aktivierung mit klaren Hinweisen, wenn der Zugriff
 * verweigert oder durch die Browser-/iframe-Richtlinie blockiert ist.
 */

// Läuft die App in einem eingebetteten iframe (z. B. Preview ohne allow="microphone")?
function isEmbedded() {
    try {
        return window.self !== window.top;
    } catch {
        return true; // Cross-Origin-Zugriff blockiert → wir sind eingebettet
    }
}

export default function VoiceRecordButton({ onText, disabled, className = "" }) {
    const [state, setState] = useState("idle"); // idle | recording | processing
    const [permission, setPermission] = useState("unknown"); // unknown | granted | denied | blocked
    const [showHelp, setShowHelp] = useState(false);
    const [liveText, setLiveText] = useState(""); // Echtzeit-Text während der Aufnahme (Jul-2026)
    const recRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const srRef = useRef(null);        // Browser-Spracherkennung (Live-Text)
    const finalTextRef = useRef("");   // bereits final erkannter Text

    // Vorab den Berechtigungsstatus abfragen (sofern Permissions-API verfügbar).
    useEffect(() => {
        let cancelled = false;
        if (navigator.permissions?.query) {
            navigator.permissions
                .query({ name: "microphone" })
                .then((res) => {
                    if (cancelled) return;
                    setPermission(res.state === "prompt" ? "unknown" : res.state);
                    res.onchange = () => setPermission(res.state === "prompt" ? "unknown" : res.state);
                })
                .catch(() => { /* Permissions-API kennt 'microphone' evtl. nicht */ });
        }
        return () => { cancelled = true; };
    }, []);

    const stop = () => {
        if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    };

    const handleGetUserMediaError = (err) => {
        const name = err?.name || "";
        const msg = (err?.message || "").toLowerCase();
        // Jul-2026 (Bugfix): Ist die Berechtigung nachweislich erteilt (z. B. Wake-
        // Word „Hey Martin" läuft), ist ein NotAllowedError nur ein vorübergehender
        // Gerätekonflikt — KEINE „Mikrofon aktivieren"-Hilfe anzeigen.
        if (permission === "granted" && (name === "NotAllowedError" || name === "NotReadableError" || name === "TrackStartError")) {
            toast.error("Mikrofon ist gerade belegt (z. B. durch die Sprachaktivierung). Bitte kurz warten und erneut tippen.");
            return;
        }
        // iframe-/Permissions-Policy-Block: im eingebetteten iframe scheitert der
        // Zugriff selbst bei erteilter Browser-Berechtigung. Daher gilt im iframe
        // jeder NotAllowedError als „blockiert" → Hinweis „in neuem Tab öffnen".
        const policyBlock = msg.includes("permissions policy") || msg.includes("permission policy") || msg.includes("disallowed");
        if ((name === "NotAllowedError" || name === "SecurityError") && (policyBlock || isEmbedded())) {
            setPermission("blocked");
            setShowHelp(true);
            toast.error("Mikrofon ist in dieser eingebetteten Vorschau blockiert. Bitte die App in einem eigenen Tab öffnen.", { duration: 8000 });
            return;
        }
        if (name === "NotAllowedError" || name === "SecurityError") {
            setPermission("denied");
            setShowHelp(true);
            toast.error("Mikrofon-Zugriff verweigert. So aktivierst du ihn:", {
                description: "Klicke links in der Adressleiste auf das Schloss-/Kamera-Symbol → Mikrofon → „Zulassen“, dann Seite neu laden.",
                duration: 8000,
            });
            return;
        }
        if (name === "NotFoundError" || name === "DevicesNotFoundError") {
            toast.error("Kein Mikrofon gefunden. Bitte ein Mikrofon anschließen.");
            return;
        }
        if (name === "NotReadableError" || name === "TrackStartError") {
            toast.error("Mikrofon wird von einer anderen App belegt. Bitte andere Programme schließen.");
            return;
        }
        toast.error("Mikrofon-Zugriff fehlgeschlagen. Bitte erneut versuchen.");
    };

    const startLiveRecognition = () => {
        finalTextRef.current = "";
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) return; // Live-Text optional — Whisper-Transkription übernimmt sonst
        try {
            const sr = new SR();
            sr.lang = "de-DE";
            sr.continuous = true;
            sr.interimResults = true;
            sr.onresult = (e) => {
                let interim = "";
                for (let i = e.resultIndex; i < e.results.length; i++) {
                    const txt = e.results[i][0].transcript;
                    if (e.results[i].isFinal) finalTextRef.current = `${finalTextRef.current} ${txt}`.trim();
                    else interim += txt;
                }
                setLiveText(`${finalTextRef.current} ${interim}`.trim());
            };
            sr.onerror = () => { /* Live-Text optional */ };
            srRef.current = sr;
            sr.start();
        } catch { /* Live-Text optional */ }
    };

    const start = async () => {
        if (state === "recording") { stop(); return; }
        setShowHelp(false);
        if (!navigator.mediaDevices?.getUserMedia) {
            // Nicht-sicherer Kontext (kein HTTPS) oder zu alter Browser.
            if (!window.isSecureContext) {
                toast.error("Mikrofon benötigt eine sichere (HTTPS-)Verbindung.");
            } else {
                toast.error("Mikrofon wird von diesem Browser nicht unterstützt.");
            }
            return;
        }
        // Jul-2026 (Bugfix): Wake-Word-Erkennung („Hey Martin") hält das exklusive
        // Mikrofon → vor der Aufnahme pausieren, damit getUserMedia nicht scheitert.
        window.dispatchEvent(new CustomEvent("tw-wake-sr-pause"));
        await new Promise((r) => setTimeout(r, 250));
        try {
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch {
                // Mikro evtl. noch belegt (Erkennung gibt es verzögert frei) → 1× erneut.
                await new Promise((r) => setTimeout(r, 500));
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            }
            setPermission("granted");
            streamRef.current = stream;
            chunksRef.current = [];
            const rec = new MediaRecorder(stream);
            recRef.current = rec;
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                streamRef.current?.getTracks().forEach((t) => t.stop());
                try { srRef.current?.stop(); } catch { /* */ }
                window.dispatchEvent(new CustomEvent("tw-wake-sr-resume"));
                setLiveText("");
                // Echtzeit-Erkennung hat bereits Text geliefert → sofort verwenden,
                // kein Warten auf Whisper (Jul-2026, User-Wunsch: Text 1:1 zur Sprache).
                const live = finalTextRef.current.trim();
                if (live) { onText?.(live); setState("idle"); return; }
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                if (blob.size < 800) { setState("idle"); return; }
                setState("processing");
                try {
                    const fd = new FormData();
                    fd.append("file", blob, "aufnahme.webm");
                    const { data } = await api.post("/assistant/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    if (data?.text) onText?.(data.text);
                    else toast.error("Keine Sprache erkannt.");
                } catch { toast.error("Transkription fehlgeschlagen."); }
                finally { setState("idle"); }
            };
            rec.start();
            startLiveRecognition();
            setState("recording");
        } catch (err) {
            window.dispatchEvent(new CustomEvent("tw-wake-sr-resume"));
            handleGetUserMediaError(err);
            setState("idle");
        }
    };

    const denied = permission === "denied" || permission === "blocked";

    return (
        <div className="relative shrink-0">
            {state === "recording" && (
                <div
                    className="absolute bottom-12 right-0 z-[120] w-72 bg-[#111827] text-white rounded-md shadow-2xl p-3 text-left animate-in fade-in slide-in-from-bottom-2 duration-200"
                    data-testid="voice-live-transcript"
                >
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#FFCC00] mb-1 flex items-center gap-1.5">
                        <Mic className="w-3 h-3 animate-pulse text-red-400" /> Ich höre zu …
                    </p>
                    <p className="text-xs leading-snug max-h-24 overflow-y-auto">
                        {liveText || <span className="text-gray-400">Sprich einfach – dein Text erscheint hier sofort.</span>}
                    </p>
                </div>
            )}
            <button
                type="button"
                onClick={start}
                disabled={disabled || state === "processing"}
                className={`h-10 w-10 flex items-center justify-center rounded-sm border shrink-0 transition-colors ${
                    state === "recording"
                        ? "bg-red-600 border-red-600 text-white animate-pulse"
                        : denied
                            ? "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100"
                            : "border-gray-300 hover:bg-gray-50 text-gray-700"
                } ${className}`}
                title={
                    state === "recording" ? "Aufnahme stoppen"
                        : denied ? "Mikrofon blockiert — zum Aktivieren tippen"
                            : "Per Sprache aufnehmen"
                }
                data-testid="voice-record-btn"
            >
                {state === "processing" ? <Loader2 className="w-4 h-4 animate-spin" />
                    : state === "recording" ? <Square className="w-4 h-4" />
                        : denied ? <MicOff className="w-4 h-4" />
                            : <Mic className="w-4 h-4" />}
            </button>

            {showHelp && denied && (
                <div
                    className="absolute bottom-12 right-0 z-[120] w-72 bg-white rounded-md shadow-2xl border border-amber-200 p-3 text-left animate-in fade-in slide-in-from-bottom-2 duration-200"
                    data-testid="voice-permission-help"
                >
                    <div className="flex items-start gap-2">
                        <MicOff className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs font-bold text-gray-800 mb-1">Mikrofon aktivieren</p>
                            {permission === "blocked" ? (
                                <p className="text-[11px] text-gray-600 leading-snug">
                                    Das Mikrofon ist in dieser eingebetteten Vorschau gesperrt. Öffne die App in
                                    einem eigenen Browser-Tab und erlaube dort den Mikrofon-Zugriff.
                                </p>
                            ) : (
                                <ol className="text-[11px] text-gray-600 leading-snug list-decimal pl-3.5 space-y-0.5">
                                    <li>Klicke links in der Adressleiste auf das Schloss-/Kamera-Symbol.</li>
                                    <li>Stelle „Mikrofon" auf <span className="font-semibold">Zulassen</span>.</li>
                                    <li>Lade die Seite neu und versuche es erneut.</li>
                                </ol>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                                {permission === "blocked" && (
                                    <a
                                        href={window.location.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[#111827] bg-[#FFCC00] hover:bg-yellow-300 rounded-sm px-2 py-1 transition-colors"
                                        data-testid="voice-open-newtab"
                                    >
                                        Im neuen Tab öffnen <ExternalLink className="w-3 h-3" />
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={() => window.location.reload()}
                                    className="text-[11px] font-semibold text-gray-700 underline"
                                    data-testid="voice-reload-page"
                                >
                                    Neu laden
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowHelp(false)}
                                    className="ml-auto text-[11px] text-gray-400 hover:text-gray-700"
                                    data-testid="voice-help-close"
                                >
                                    Schließen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
